// client/src/services/apiService.ts (converted from .js, ST-00230)
//
// The axios client of Order Management (services/orderService.js) and Web Push
// (services/pushService.js). Cookie-session auth (httpOnly) — no client-held token; shared
// single-flight refresh + CSRF via services/authClient.ts. Since PLT-12 it also masks its
// responses in demo mode (utils/demoMode), like the shared `api` instance.
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { installAuthInterceptors } from './authClient';
import { installDemoMasking } from '../utils/demoMode';

// Back-compat no-op — auth is cookie-based now, nothing to initialize.
export const initializeApiService = (): void => {};

// Create axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.REACT_APP_API_URL || 'http://localhost:5002',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// withCredentials + CSRF header + single-flight refresh on 401 (shared module)
installAuthInterceptors(apiClient);

// Demo mode (PLT-12): Order Management showed every real order, site, company, vehicle and
// driver, because only the shared `api` instance masked its responses.
installDemoMasking(apiClient);

// Response interceptor: log 403s (401/refresh handled by authClient)
apiClient.interceptors.response.use(
  (response) => response,
  (error: { response?: { status?: number; data?: unknown } }) => {
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data);
    }
    return Promise.reject(error);
  }
);

// Export the configured axios instance
export default apiClient;

/** Request body: any JSON-able value (endpoint shapes are not modelled here). */
type Body = unknown;

// Convenience methods
export const api = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    apiClient.get<T>(url, config),
  post: <T = unknown>(
    url: string,
    data?: Body,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> => apiClient.post<T>(url, data, config),
  put: <T = unknown>(
    url: string,
    data?: Body,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> => apiClient.put<T>(url, data, config),
  delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    apiClient.delete<T>(url, config),
  patch: <T = unknown>(
    url: string,
    data?: Body,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> => apiClient.patch<T>(url, data, config),
};
